import type { Rng } from '../rng/seededRandom';
import { normalizeDir, type Dir2 } from './aiNavigation';
import { centre, eyePosition, sightBlocked } from './combat';
import type { TileMap } from './tilemap';
import type { BattleState, Fighter, Vec3 } from './types';

/** The world at one tick, limited to what a player could see. */
export interface Snapshot {
  time: number;
  enemyPos: Vec3;
  enemyVel: Vec3;
  enemyHp: number;
  shots: SeenShot[];
}

export interface SeenShot {
  id: number;
  pos: Vec3;
  vel: Vec3;
  radius: number;
}

export interface PerceptionMemory {
  /** Recent snapshots, oldest first; the AI acts on the one its reaction delay allows. */
  history: Snapshot[];
  /** Whether each shot in flight was noticed, decided once when it first came into view. */
  noticed: Map<number, boolean>;
  lastEnemyShotId: number;
  /** Battle time the enemy was last seen shooting, to guess when it can shoot again. */
  enemyFiredAt: number;
}

/** A shot that will hit unless the fighter moves; `sidestep` is null when either side is as good. */
export interface Threat {
  sidestep: Dir2 | null;
  timeToImpact: number;
}

/** What the AI believes this tick: a delayed, extrapolated picture of the enemy and the shots in the air. */
export interface View {
  enemyPos: Vec3;
  enemyVel: Vec3;
  enemyCentre: Vec3;
  enemyEye: Vec3;
  enemyHp: number;
  /** Horizontal distance to the believed enemy position. */
  dist: number;
  /** Line of sight from the fighter's eye to the believed enemy centre. */
  los: boolean;
  threats: Threat[];
  /** Estimated seconds until the enemy can shoot again, from its attack interval and its last shot. */
  enemyCanShootIn: number;
}

export interface Senses {
  reaction: number;
  anticipation: number;
  notice: number;
}

/** The enemy's visible state; its cooldown, stun and aim stay hidden from the AI. */
export type VisibleEnemy = Pick<Fighter, 'pos' | 'vel' | 'hp' | 'height' | 'attackInterval'>;

/** Shots further off than this many seconds are watched, not dodged yet. */
const THREAT_HORIZON = 1;
/** A shot that would pass this close to the hit box still counts as a threat: the picture may be stale. */
const THREAT_MARGIN = 0.4;
/** A shot this far off the fighter's vertical line has a side to step away from. */
const SIDE_EPSILON = 0.05;

/**
 * Records the true state and returns the view the AI is allowed to act on: the snapshot from
 * `reaction` seconds ago, with the enemy extrapolated along its velocity by `anticipation`.
 */
export function perceive(
  map: TileMap,
  state: BattleState,
  me: Fighter,
  enemy: VisibleEnemy,
  memory: PerceptionMemory,
  senses: Senses,
  rng: Rng,
): View {
  memory.history.push({
    time: state.time,
    enemyPos: { ...enemy.pos },
    enemyVel: { ...enemy.vel },
    enemyHp: enemy.hp,
    shots: state.projectiles
      .filter((p) => p.owner !== me.id)
      .map((p) => ({ id: p.id, pos: { ...p.pos }, vel: { ...p.vel }, radius: p.radius })),
  });
  const cutoff = state.time - senses.reaction;
  while (memory.history.length > 1 && memory.history[1]!.time <= cutoff) memory.history.shift();
  const seen = memory.history[0]!;
  const age = state.time - seen.time;

  const k = age * senses.anticipation;
  const enemyPos = {
    x: seen.enemyPos.x + seen.enemyVel.x * k,
    y: seen.enemyPos.y,
    z: seen.enemyPos.z + seen.enemyVel.z * k,
  };
  const enemyCentre = { x: enemyPos.x, y: enemyPos.y + enemy.height / 2, z: enemyPos.z };
  const enemyEye = { x: enemyPos.x, y: enemyPos.y + enemy.height * 0.9, z: enemyPos.z };
  const eye = eyePosition(me);
  const los = !sightBlocked(map, eye, enemyCentre);

  for (const shot of seen.shots) {
    if (shot.id > memory.lastEnemyShotId) {
      memory.lastEnemyShotId = shot.id;
      memory.enemyFiredAt = seen.time;
    }
  }
  const enemyCanShootIn = Math.max(0, enemy.attackInterval - (state.time - memory.enemyFiredAt));

  return {
    enemyPos,
    enemyVel: { ...seen.enemyVel },
    enemyCentre,
    enemyEye,
    enemyHp: seen.enemyHp,
    dist: Math.hypot(enemyPos.x - me.pos.x, enemyPos.z - me.pos.z),
    los,
    threats: threats(map, me, seen, age, memory, senses, rng),
    enemyCanShootIn,
  };
}

/** Shots on course for the fighter that it has noticed and can see. */
function threats(
  map: TileMap,
  me: Fighter,
  seen: Snapshot,
  age: number,
  memory: PerceptionMemory,
  senses: Senses,
  rng: Rng,
): Threat[] {
  const out: Threat[] = [];
  const myCentre = centre(me);
  const eye = eyePosition(me);
  const inFlight = new Set<number>();
  for (const shot of seen.shots) {
    inFlight.add(shot.id);
    let noticed = memory.noticed.get(shot.id);
    if (noticed === undefined) {
      noticed = rng.next() < senses.notice;
      memory.noticed.set(shot.id, noticed);
    }
    if (!noticed) continue;
    const speed = Math.hypot(shot.vel.x, shot.vel.y, shot.vel.z);
    if (speed < 1e-3) continue;
    // Shots fly straight, so once seen their present position is known despite the delay.
    const pos = {
      x: shot.pos.x + shot.vel.x * age,
      y: shot.pos.y + shot.vel.y * age,
      z: shot.pos.z + shot.vel.z * age,
    };
    const rel = { x: myCentre.x - pos.x, y: myCentre.y - pos.y, z: myCentre.z - pos.z };
    const along = (rel.x * shot.vel.x + rel.y * shot.vel.y + rel.z * shot.vel.z) / speed;
    if (along <= 0) continue;
    const timeToImpact = along / speed;
    if (timeToImpact > THREAT_HORIZON) continue;
    // Where the shot is when nearest the centre, against the hit box (a tall box, not a sphere:
    // a shot grazing the head still hurts) grown by the radius and a margin.
    const near = {
      x: pos.x + (shot.vel.x / speed) * along,
      y: pos.y + (shot.vel.y / speed) * along,
      z: pos.z + (shot.vel.z / speed) * along,
    };
    const slack = shot.radius + THREAT_MARGIN;
    if (Math.abs(near.x - me.pos.x) > me.halfWidth + slack) continue;
    if (Math.abs(near.z - me.pos.z) > me.halfWidth + slack) continue;
    if (near.y < me.pos.y - slack || near.y > me.pos.y + me.height + slack) continue;
    if (sightBlocked(map, eye, pos)) continue;
    const offLine = { x: me.pos.x - near.x, z: me.pos.z - near.z };
    const sidestep =
      Math.hypot(offLine.x, offLine.z) > SIDE_EPSILON ? normalizeDir(offLine.x, offLine.z) : null;
    out.push({ sidestep, timeToImpact });
  }
  for (const id of [...memory.noticed.keys()]) if (!inFlight.has(id)) memory.noticed.delete(id);
  return out;
}
